/**
 * @file src/services/settings/GlobalSettingsService.js
 * @description Global Settings Service - Manages Header, Footer, Left Menu, Cache, Branding Settings
 *
 * Implements:
 * - REC-GS-001: Tabbed Settings Architecture
 * - REC-GS-002: Logo Upload with validation
 * - REC-GS-003: Real-time Preview Mode
 * - REC-GS-004: Settings Inheritance Indicator
 * - REC-GS-005: Form Validation Strategy
 * - REC-GS-006: Responsive Image Handling
 * - REC-GS-007: Unsaved Changes Protection
 * - REC-GS-008: Settings Export/Import
 * - REC-GS-012: Settings Persistence Strategy
 * - R10: Default Logo Source (using default assets)
 * - Branding Settings: Customer Profile Integration
 *
 * Storage Strategy:
 * - IndexedDB for logo images (large binary data)
 * - localStorage for text settings (fast access)
 * - Optional backend sync for multi-device support
 */

import {
  safeGetItem,
  safeGetJSON,
  safeSetItem,
  safeSetJSON,
} from "../../utils/storage";
// import DEFAULT_LOGO from "../../assets/images/omni_logo.png";
// import DEFAULT_CUSTOMER_LOGO from "../../assets/images/digitalTwinLogo.png";

// Default constants
const DEFAULT_COMPANY_NAME = "OomniEye";
const DEFAULT_COMPANY_CAPTION = "NEXT-GEN COMMAND & CONTROL";
const DEFAULT_LOGO_DIMENSIONS = { width: 72, height: 72 };
const DEFAULT_FOOTER_COPYRIGHT = "© 2026 OomniEye. All rights reserved.";

// Default settings
export const DEFAULT_HEADER_SETTINGS = {
  logo: "",
  logoWidth: DEFAULT_LOGO_DIMENSIONS.width,
  logoHeight: DEFAULT_LOGO_DIMENSIONS.height,
  companyName: DEFAULT_COMPANY_NAME,
  companyCaption: DEFAULT_COMPANY_CAPTION,
  showLogo: true,
  showCompanyName: true,
  showCompanyCaption: true,
  lastModified: new Date().toISOString(),
};

export const DEFAULT_FOOTER_SETTINGS = {
  copyrightText: DEFAULT_FOOTER_COPYRIGHT,
  showFooter: true,
  links: [],
  lastModified: new Date().toISOString(),
};

export const DEFAULT_LEFT_MENU_SETTINGS = {
  collapsed: true,
  width: 180,
  collapsedWidth: 64,
  showIcons: true,
  showLabels: false,
  lastModified: new Date().toISOString(),
};

export const DEFAULT_CACHE_SETTINGS = {
  maxSize: 500,
  maxAge: 30,
  enableCompression: true,
  enableIntegrity: true,
  preCacheOnWifi: true,
  lastModified: new Date().toISOString(),
};

export const DEFAULT_BRANDING_SETTINGS = {
  customerName: "Digital Twin Solutions",
  customerLogo: "",
  customerLogoWidth: 48,
  customerLogoHeight: 48,
  customerTextColor: "#FF9800",
  showCustomerLogo: true,
  showCustomerName: true,
  lastModified: new Date().toISOString(),
};

// Storage keys
const STORAGE_KEY_PREFIX = "oomnieye_global_settings_";
const HEADER_KEY = `${STORAGE_KEY_PREFIX}header`;
const FOOTER_KEY = `${STORAGE_KEY_PREFIX}footer`;
const LEFT_MENU_KEY = `${STORAGE_KEY_PREFIX}left_menu`;
const CACHE_KEY = `${STORAGE_KEY_PREFIX}cache`;
const BRANDING_KEY = `${STORAGE_KEY_PREFIX}branding`;

/** Must match api.js so hydrate can read API cache */
const SITE_SETTINGS_CACHE_KEY = "oomnieye_site_settings_api_cache";

/**
 * Sync hydrate: write API cache into per-section localStorage so first paint shows cached data on refresh.
 * Call once before any component that reads header/footer/leftMenu/branding (e.g. at start of DashboardLayout).
 */
export const hydrateFromSiteSettingsCache = () => {
  try {
    const raw = safeGetJSON(SITE_SETTINGS_CACHE_KEY);
    if (!raw || !raw.response) return;
    const response = raw.response;
    const value = response?.data?.value ?? response?.value ?? response ?? {};
    if (value.header) {
      safeSetJSON(HEADER_KEY, value.header);
      if (value.header.logo && typeof value.header.logo === "string") {
        safeSetItem(`${HEADER_KEY}_logo`, value.header.logo);
      }
    }
    if (value.footer) safeSetJSON(FOOTER_KEY, value.footer);
    if (value.leftMenu) safeSetJSON(LEFT_MENU_KEY, value.leftMenu);
    if (value.cache) safeSetJSON(CACHE_KEY, value.cache);
    if (value.branding) {
      safeSetJSON(BRANDING_KEY, value.branding);
      if (
        value.branding.customerLogo &&
        typeof value.branding.customerLogo === "string"
      ) {
        safeSetItem(`${BRANDING_KEY}_logo`, value.branding.customerLogo);
      }
    }
  } catch {
    // ignore
  }
};

/**
 * Sync read footer settings from localStorage. Use for initial state so first paint shows cache.
 */
export const getFooterSettingsSync = () => {
  try {
    const stored = safeGetJSON(FOOTER_KEY);
    return stored
      ? { ...DEFAULT_FOOTER_SETTINGS, ...stored }
      : { ...DEFAULT_FOOTER_SETTINGS };
  } catch {
    return { ...DEFAULT_FOOTER_SETTINGS };
  }
};

/**
 * Sync read left menu settings from localStorage. Use for initial state so first paint shows cache.
 */
export const getLeftMenuSettingsSync = () => {
  try {
    const stored = safeGetJSON(LEFT_MENU_KEY);
    return stored
      ? { ...DEFAULT_LEFT_MENU_SETTINGS, ...stored }
      : { ...DEFAULT_LEFT_MENU_SETTINGS };
  } catch {
    return { ...DEFAULT_LEFT_MENU_SETTINGS };
  }
};

/**
 * Sync read branding settings from localStorage (and logo fallback). Use for initial state so first paint shows cache.
 */
export const getBrandingSettingsSync = () => {
  try {
    const stored = safeGetJSON(BRANDING_KEY);
    const settings = stored
      ? { ...DEFAULT_BRANDING_SETTINGS, ...stored }
      : { ...DEFAULT_BRANDING_SETTINGS };
    const logoFallback = safeGetItem(`${BRANDING_KEY}_logo`);
    if (
      logoFallback &&
      (!settings.customerLogo ||
        settings.customerLogo === DEFAULT_BRANDING_SETTINGS.customerLogo)
    ) {
      settings.customerLogo = logoFallback;
    }
    return settings;
  } catch {
    return { ...DEFAULT_BRANDING_SETTINGS };
  }
};

/**
 * Sync read cache settings from localStorage. Use for initial state so first paint shows cache.
 */
export const getCacheSettingsSync = () => {
  try {
    const stored = safeGetJSON(CACHE_KEY);
    return stored
      ? { ...DEFAULT_CACHE_SETTINGS, ...stored }
      : { ...DEFAULT_CACHE_SETTINGS };
  } catch {
    return { ...DEFAULT_CACHE_SETTINGS };
  }
};

// IndexedDB store name
const GLOBAL_SETTINGS_STORE = "global_settings";

// Track if we've already warned about IndexedDB unavailability
let hasWarnedAboutIndexedDB = false;
let hasWarnedAboutBrandingIndexedDB = false;
let hasWarnedAboutHeaderIndexedDB = false;

/**
 * Initialize IndexedDB database
 * Returns null if IndexedDB is not available (iframe environment)
 */
const initDB = async () => {
  try {
    if (!window.indexedDB) {
      if (!hasWarnedAboutIndexedDB) {
        console.info(
          "ℹ️ [GlobalSettingsService] IndexedDB not available - using localStorage fallback",
        );
        hasWarnedAboutIndexedDB = true;
      }
      return null;
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Database initialization timeout"));
      }, 3000);

      const request = indexedDB.open("OomniEyeDB", 1);

      request.onerror = () => {
        clearTimeout(timeout);
        if (!hasWarnedAboutIndexedDB) {
          console.warn(
            "⚠️ [GlobalSettingsService] Database initialization failed - using localStorage fallback",
          );
          hasWarnedAboutIndexedDB = true;
        }
        resolve(null);
      };

      request.onsuccess = () => {
        clearTimeout(timeout);
        const db = request.result;

        // Check if store exists
        if (!db.objectStoreNames.contains(GLOBAL_SETTINGS_STORE)) {
          // Store doesn't exist, create it
          db.close();
          const upgradeRequest = indexedDB.open("OomniEyeDB", 2);
          upgradeRequest.onupgradeneeded = (event) => {
            const upgradeDb = event.target.result;
            if (!upgradeDb.objectStoreNames.contains(GLOBAL_SETTINGS_STORE)) {
              upgradeDb.createObjectStore(GLOBAL_SETTINGS_STORE, {
                keyPath: "id",
              });
            }
          };
          upgradeRequest.onsuccess = () => resolve(upgradeRequest.result);
          upgradeRequest.onerror = () => resolve(null);
        } else {
          resolve(db);
        }
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(GLOBAL_SETTINGS_STORE)) {
          db.createObjectStore(GLOBAL_SETTINGS_STORE, { keyPath: "id" });
        }
      };
    });
  } catch (error) {
    if (!hasWarnedAboutIndexedDB) {
      console.warn(
        "⚠️ [GlobalSettingsService] Database initialization failed - using localStorage fallback:",
        error,
      );
      hasWarnedAboutIndexedDB = true;
    }
    return null;
  }
};

/**
 * REC-GS-002: Validate and resize image
 * Validates file type, size, and resizes to target dimensions
 */
export const validateAndResizeImage = async (
  file,
  targetWidth = 72,
  targetHeight = 72,
) => {
  return new Promise((resolve, reject) => {
    // REC-GS-005: File validation
    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      reject(new Error("Invalid file type. Please upload PNG, JPG, or WebP."));
      return;
    }

    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      reject(new Error("File size exceeds 2MB limit."));
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // REC-GS-002: Resize to target dimensions
        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }

        // Clear canvas with transparent background
        ctx.clearRect(0, 0, targetWidth, targetHeight);

        // Calculate dimensions to maintain aspect ratio
        const aspectRatio = img.width / img.height;
        let drawWidth = targetWidth;
        let drawHeight = targetHeight;
        let offsetX = 0;
        let offsetY = 0;

        if (aspectRatio > 1) {
          // Wider than tall
          drawHeight = targetWidth / aspectRatio;
          offsetY = (targetHeight - drawHeight) / 2;
        } else {
          // Taller than wide
          drawWidth = targetHeight * aspectRatio;
          offsetX = (targetWidth - drawWidth) / 2;
        }

        // Draw image centered
        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

        // REC-GS-006: Convert to Base64 with quality optimization
        const base64 = canvas.toDataURL("image/png", 0.95);
        resolve(base64);
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      img.src = e.target?.result;
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * REC-GS-012: Save header settings to localStorage + IndexedDB
 */
const toBool = (v) => v === true || v === "true";

/**
 * Sync read header settings from localStorage only (no IndexedDB). Use for initial state so first paint shows cache.
 */
export const getHeaderSettingsSync = () => {
  try {
    const stored = safeGetJSON(HEADER_KEY);
    const settings = stored
      ? { ...DEFAULT_HEADER_SETTINGS, ...stored }
      : { ...DEFAULT_HEADER_SETTINGS };
    const logoFallback = safeGetItem(`${HEADER_KEY}_logo`);
    if (
      logoFallback &&
      (!settings.logo || settings.logo === DEFAULT_HEADER_SETTINGS.logo)
    ) {
      settings.logo = logoFallback;
    }
    if (settings.showLogo !== undefined)
      settings.showLogo = toBool(settings.showLogo);
    if (settings.showCompanyName !== undefined)
      settings.showCompanyName = toBool(settings.showCompanyName);
    if (settings.showCompanyCaption !== undefined)
      settings.showCompanyCaption = toBool(settings.showCompanyCaption);
    return settings;
  } catch {
    return { ...DEFAULT_HEADER_SETTINGS };
  }
};

export const saveHeaderSettings = async (settings) => {
  try {
    const current = await loadHeaderSettings();
    const raw = {
      ...current,
      ...settings,
      lastModified: new Date().toISOString(),
    };
    const updated = {
      ...raw,
      showLogo:
        raw.showLogo !== undefined ? toBool(raw.showLogo) : raw.showLogo,
      showCompanyName:
        raw.showCompanyName !== undefined
          ? toBool(raw.showCompanyName)
          : raw.showCompanyName,
      showCompanyCaption:
        raw.showCompanyCaption !== undefined
          ? toBool(raw.showCompanyCaption)
          : raw.showCompanyCaption,
    };

    // Save to localStorage (fast access for text data)
    const { logo, ...textSettings } = updated;
    safeSetJSON(HEADER_KEY, textSettings);

    // Save logo to IndexedDB (use default when logo not provided)
    const logoToSave = logo ?? "";
    try {
      const db = await initDB();
      if (db) {
        const transaction = db.transaction(
          [GLOBAL_SETTINGS_STORE],
          "readwrite",
        );
        const store = transaction.objectStore(GLOBAL_SETTINGS_STORE);
        await new Promise((resolve, reject) => {
          const request = store.put({ id: "header_logo", data: logoToSave });
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      } else {
        safeSetItem(`${HEADER_KEY}_logo`, logoToSave);
      }
    } catch (error) {
      console.warn(
        "⚠️ [saveHeaderSettings] Failed to save logo to IndexedDB:",
        error,
      );
      safeSetItem(`${HEADER_KEY}_logo`, logoToSave);
    }
    // Always write to localStorage so getHeaderSettingsSync() can show logo on first paint (no IndexedDB in sync path)
    if (logoToSave) safeSetItem(`${HEADER_KEY}_logo`, logoToSave);

    // Dispatch event for real-time updates (REC-GS-003)
    window.dispatchEvent(
      new CustomEvent("headerSettingsUpdated", { detail: updated }),
    );

    console.log("✅ Header settings saved:", updated);
  } catch (error) {
    console.error("❌ Failed to save header settings:", error);
    throw error;
  }
};

/**
 * Load header settings from localStorage + IndexedDB
 */
export const loadHeaderSettings = async () => {
  try {
    // Load text settings from localStorage
    const stored = safeGetJSON(HEADER_KEY);
    let settings = stored
      ? { ...DEFAULT_HEADER_SETTINGS, ...stored }
      : { ...DEFAULT_HEADER_SETTINGS };

    // Load logo from IndexedDB
    try {
      const db = await initDB();
      console.log("db", db);

      if (db) {
        const transaction = db.transaction([GLOBAL_SETTINGS_STORE], "readonly");
        const store = transaction.objectStore(GLOBAL_SETTINGS_STORE);
        const request = store.get("header_logo");

        const logoData = await new Promise((resolve, reject) => {
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        if (logoData?.data) {
          settings.logo = logoData.data;
          safeSetItem(`${HEADER_KEY}_logo`, logoData.data);
        }
      } else {
        if (!hasWarnedAboutHeaderIndexedDB) {
          console.info(
            "ℹ️ [loadHeaderSettings] Using localStorage fallback for logo",
          );
          hasWarnedAboutHeaderIndexedDB = true;
        }
        const logoFallback = localStorage.getItem(`${HEADER_KEY}_logo`);
        if (logoFallback) {
          settings.logo = logoFallback;
        }
      }
    } catch (error) {
      const logoFallback = localStorage.getItem(`${HEADER_KEY}_logo`);
      if (logoFallback) {
        settings.logo = logoFallback;
      }
    }

    // Normalize booleans so "false"/"true" from API or old storage behave correctly
    if (settings.showLogo !== undefined)
      settings.showLogo = toBool(settings.showLogo);
    if (settings.showCompanyName !== undefined)
      settings.showCompanyName = toBool(settings.showCompanyName);
    if (settings.showCompanyCaption !== undefined)
      settings.showCompanyCaption = toBool(settings.showCompanyCaption);

    console.log("settings", settings);

    return settings;
  } catch (error) {
    console.error("❌ Failed to load header settings:", error);
    return { ...DEFAULT_HEADER_SETTINGS };
  }
};

/**
 * Save footer settings
 */
export const saveFooterSettings = async (settings) => {
  try {
    const current = await loadFooterSettings();
    const updated = {
      ...current,
      ...settings,
      lastModified: new Date().toISOString(),
    };

    safeSetJSON(FOOTER_KEY, updated);
    window.dispatchEvent(
      new CustomEvent("footerSettingsUpdated", { detail: updated }),
    );

    console.log("✅ Footer settings saved:", updated);
  } catch (error) {
    console.error("❌ Failed to save footer settings:", error);
    throw error;
  }
};

/**
 * Load footer settings
 */
export const loadFooterSettings = async () => {
  try {
    const stored = safeGetJSON(FOOTER_KEY);
    return stored
      ? { ...DEFAULT_FOOTER_SETTINGS, ...stored }
      : { ...DEFAULT_FOOTER_SETTINGS };
  } catch (error) {
    console.error("❌ Failed to load footer settings:", error);
    return { ...DEFAULT_FOOTER_SETTINGS };
  }
};

/**
 * Load left menu settings
 */
export const loadLeftMenuSettings = async () => {
  try {
    const stored = safeGetJSON(LEFT_MENU_KEY);
    return stored
      ? { ...DEFAULT_LEFT_MENU_SETTINGS, ...stored }
      : { ...DEFAULT_LEFT_MENU_SETTINGS };
  } catch (error) {
    console.error("❌ Failed to load left menu settings:", error);
    return { ...DEFAULT_LEFT_MENU_SETTINGS };
  }
};

/**
 * Save left menu settings
 */
export const saveLeftMenuSettings = async (settings) => {
  try {
    const current = await loadLeftMenuSettings();
    const updated = {
      ...current,
      ...settings,
      lastModified: new Date().toISOString(),
    };

    safeSetJSON(LEFT_MENU_KEY, updated);
    window.dispatchEvent(
      new CustomEvent("leftMenuSettingsUpdated", { detail: updated }),
    );

    console.log("✅ Left menu settings saved:", updated);
  } catch (error) {
    console.error("❌ Failed to save left menu settings:", error);
    throw error;
  }
};

/**
 * Save cache settings
 */
export const saveCacheSettings = async (settings) => {
  try {
    const current = await loadCacheSettings();
    const updated = {
      ...current,
      ...settings,
      lastModified: new Date().toISOString(),
    };

    safeSetJSON(CACHE_KEY, updated);
    window.dispatchEvent(
      new CustomEvent("cacheSettingsUpdated", { detail: updated }),
    );

    console.log("✅ Cache settings saved:", updated);
  } catch (error) {
    console.error("❌ Failed to save cache settings:", error);
    throw error;
  }
};

/**
 * Load cache settings
 */
export const loadCacheSettings = async () => {
  try {
    const stored = safeGetJSON(CACHE_KEY);
    return stored
      ? { ...DEFAULT_CACHE_SETTINGS, ...stored }
      : { ...DEFAULT_CACHE_SETTINGS };
  } catch (error) {
    console.error("❌ Failed to load cache settings:", error);
    return { ...DEFAULT_CACHE_SETTINGS };
  }
};

/**
 * Save branding settings to localStorage + IndexedDB
 */
export const saveBrandingSettings = async (settings) => {
  try {
    const current = await loadBrandingSettings();
    const updated = {
      ...current,
      ...settings,
      lastModified: new Date().toISOString(),
    };

    // Save to localStorage (text settings)
    const { customerLogo, ...textSettings } = updated;
    safeSetJSON(BRANDING_KEY, textSettings);

    // Save customer logo to IndexedDB (use default when not provided)
    const logoToSave = customerLogo ?? "";
    try {
      const db = await initDB();
      if (db) {
        const transaction = db.transaction(
          [GLOBAL_SETTINGS_STORE],
          "readwrite",
        );
        const store = transaction.objectStore(GLOBAL_SETTINGS_STORE);
        await new Promise((resolve, reject) => {
          const request = store.put({
            id: "branding_customer_logo",
            data: logoToSave,
          });
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      } else {
        safeSetItem(`${BRANDING_KEY}_logo`, logoToSave);
      }
    } catch (dbError) {
      console.warn(
        "⚠️ IndexedDB save failed, using localStorage fallback:",
        dbError,
      );
      safeSetItem(`${BRANDING_KEY}_logo`, logoToSave);
    }
    // Always write to localStorage so getBrandingSettingsSync() can show logo on first paint (no IndexedDB in sync path)
    if (logoToSave) safeSetItem(`${BRANDING_KEY}_logo`, logoToSave);

    // Dispatch event for real-time updates
    window.dispatchEvent(
      new CustomEvent("brandingSettingsUpdated", { detail: updated }),
    );
    window.dispatchEvent(new CustomEvent("customerProfileUpdated"));

    console.log("✅ Branding settings saved:", updated);
  } catch (error) {
    console.error("❌ Failed to save branding settings:", error);
    throw error;
  }
};

/**
 * Load branding settings from localStorage + IndexedDB
 */
export const loadBrandingSettings = async () => {
  try {
    // Load text settings from localStorage
    const stored = safeGetJSON(BRANDING_KEY);
    let settings = stored
      ? { ...DEFAULT_BRANDING_SETTINGS, ...stored }
      : { ...DEFAULT_BRANDING_SETTINGS };

    // Load customer logo from IndexedDB (with fallback to localStorage)
    try {
      const db = await initDB();
      if (db) {
        const transaction = db.transaction([GLOBAL_SETTINGS_STORE], "readonly");
        const store = transaction.objectStore(GLOBAL_SETTINGS_STORE);
        const request = store.get("branding_customer_logo");

        const logoData = await new Promise((resolve, reject) => {
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });
        if (logoData?.data) {
          settings.customerLogo = logoData.data;
          safeSetItem(`${BRANDING_KEY}_logo`, logoData.data);
        }
      } else {
        if (!hasWarnedAboutBrandingIndexedDB) {
          console.info(
            "ℹ️ [loadBrandingSettings] Using localStorage fallback for logo",
          );
          hasWarnedAboutBrandingIndexedDB = true;
        }
        const logoFallback = localStorage.getItem(`${BRANDING_KEY}_logo`);
        if (logoFallback) {
          settings.customerLogo = logoFallback;
        }
      }
    } catch (error) {
      // Silent fallback to localStorage
      const logoFallback = localStorage.getItem(`${BRANDING_KEY}_logo`);
      if (logoFallback) {
        settings.customerLogo = logoFallback;
      }
    }

    return settings;
  } catch (error) {
    console.error("❌ Failed to load branding settings:", error);
    return { ...DEFAULT_BRANDING_SETTINGS };
  }
};

/**
 * Build payload for POST /user-display-settings/globalSetting
 * Send only current UI values; preserve empty/null; no placeholder or default text.
 * lastModified in ISO format; root version and lastModified.
 */
export const buildGlobalSettingsPayload = (
  header,
  footer,
  leftMenu,
  cache,
  branding,
) => {
  const now = new Date().toISOString();
  const version = process.env.REACT_APP_VERSION || "1.0.0";

  const headerPayload =
    header && typeof header === "object"
      ? { ...header, lastModified: now }
      : { lastModified: now };

  const footerPayload =
    footer && typeof footer === "object"
      ? { ...footer, lastModified: now }
      : { lastModified: now };

  const leftMenuPayload =
    leftMenu && typeof leftMenu === "object"
      ? {
          ...leftMenu,
          lastModified: now,
        }
      : { lastModified: now };

  const cachePayload =
    cache && typeof cache === "object"
      ? { ...cache, lastModified: now }
      : { lastModified: now };

  const brandingPayload =
    branding && typeof branding === "object"
      ? { ...branding, lastModified: now }
      : { lastModified: now };

  return {
    header: headerPayload,
    footer: footerPayload,
    leftMenu: leftMenuPayload,
    cache: cachePayload,
    branding: brandingPayload,
    version,
    lastModified: now,
  };
};

/**
 * REC-GS-008: Export all global settings
 */
export const exportGlobalSettings = async () => {
  const header = await loadHeaderSettings();
  const footer = await loadFooterSettings();
  const leftMenu = await loadLeftMenuSettings();
  const cache = await loadCacheSettings();
  const branding = await loadBrandingSettings();

  return {
    header,
    footer,
    leftMenu,
    cache,
    branding,
    version: "1.0.0",
    lastModified: new Date().toISOString(),
  };
};

/**
 * REC-GS-008: Import global settings
 */
export const importGlobalSettings = async (data) => {
  try {
    // Validate version compatibility
    if (data.version !== "1.0.0") {
      console.warn("⚠️ Settings version mismatch. Attempting migration...");
    }

    // Import each section
    if (data.header) await saveHeaderSettings(data.header);
    if (data.footer) await saveFooterSettings(data.footer);
    if (data.leftMenu) await saveLeftMenuSettings(data.leftMenu);
    if (data.cache) await saveCacheSettings(data.cache);
    if (data.branding) await saveBrandingSettings(data.branding);

    console.log("✅ Global settings imported successfully");
  } catch (error) {
    console.error("❌ Failed to import global settings:", error);
    throw error;
  }
};

/**
 * REC-GS-004: Reset settings to defaults
 */
export const resetToDefaults = async (section) => {
  try {
    if (!section || section === "header") {
      await saveHeaderSettings(DEFAULT_HEADER_SETTINGS);
    }
    if (!section || section === "footer") {
      await saveFooterSettings(DEFAULT_FOOTER_SETTINGS);
    }
    if (!section || section === "leftMenu") {
      await saveLeftMenuSettings(DEFAULT_LEFT_MENU_SETTINGS);
    }
    if (!section || section === "cache") {
      await saveCacheSettings(DEFAULT_CACHE_SETTINGS);
    }
    if (!section || section === "branding") {
      await saveBrandingSettings(DEFAULT_BRANDING_SETTINGS);
    }

    console.log(`✅ Settings reset to defaults: ${section || "all"}`);
  } catch (error) {
    console.error("❌ Failed to reset settings:", error);
    throw error;
  }
};

/**
 * REC-GS-004: Check if settings have been customized
 */
export const isCustomized = (current, defaults) => {
  return JSON.stringify(current) !== JSON.stringify(defaults);
};

/**
 * Get default settings for comparison
 */
export const getDefaults = () => ({
  header: DEFAULT_HEADER_SETTINGS,
  footer: DEFAULT_FOOTER_SETTINGS,
  leftMenu: DEFAULT_LEFT_MENU_SETTINGS,
  cache: DEFAULT_CACHE_SETTINGS,
  branding: DEFAULT_BRANDING_SETTINGS,
});

/**
 * Convert BrandingSettings to CustomerProfile (for backward compatibility)
 */
export const brandingToCustomerProfile = (settings) => {
  return {
    name: settings.customerName,
    logo: settings.customerLogo,
    textColor: settings.customerTextColor,
  };
};

/**
 * Convert CustomerProfile to BrandingSettings (for migration)
 */
export const customerProfileToBranding = (profile) => {
  return {
    customerName: profile.name || "",
    customerLogo: profile.logo || null,
    customerTextColor: profile.textColor || "#FF9800",
    showCustomerName: true,
    showCustomerLogo: true,
  };
};

/**
 * Load BrandingSettings as CustomerProfile
 */
export const loadBrandingAsCustomerProfile = async () => {
  try {
    const brandingSettings = await loadBrandingSettings();

    // Graceful degradation - check visibility flags
    if (
      !brandingSettings.showCustomerName &&
      !brandingSettings.showCustomerLogo
    ) {
      return null;
    }

    const profile = brandingToCustomerProfile(brandingSettings);

    // If name is still default/empty, return null
    if (!profile.name || profile.name === "Digital Twin Solutions") {
      const isCustomized =
        brandingSettings.lastModified !==
        DEFAULT_BRANDING_SETTINGS.lastModified;
      if (!isCustomized && !profile.logo) {
        return null;
      }
    }

    return profile;
  } catch (error) {
    console.error("❌ Failed to load branding as customer profile:", error);
    return null;
  }
};

/**
 * Load all header-related data in a single optimized call
 */
export const loadHeaderData = async () => {
  try {
    const [headerSettings, customerProfile] = await Promise.all([
      loadHeaderSettings(),
      loadBrandingAsCustomerProfile(),
    ]);

    return { headerSettings, customerProfile };
  } catch (error) {
    console.error("❌ [loadHeaderData] Failed to load header data:", error);
    throw error;
  }
};
